import React, { createContext, useState } from 'react'
import { enqueueSnackbar, VariantType } from 'notistack'

import ModalMessages from 'components/Modal/ModalMessages/modalMessages'

import { differenceInMilliseconds } from 'date-fns'

type Message = {
  sender: string
  content: string
  code: string
  level: VariantType
  date: Date
  enableDuplicates?: boolean
}

export type HandleDisplayMessage = (m: Message) => void

export type MessageContext = {
  handleDisplayMessage: HandleDisplayMessage
  handleToggleModalMessages: () => void
  allDisplayedMessages: Message[]
  handleClearMessages: () => void
}

const DELAY_BETWEEN_2_IDENTICAL_MESSAGES_IN_MS = 1000

const MessageContext = createContext<MessageContext>({
  handleDisplayMessage: () => {},
  handleToggleModalMessages: () => {},
  allDisplayedMessages: [],
  handleClearMessages: () => {},
})

export const MessageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isModalMessagesVisible, setModalMessagesVisible] = useState(false)
  const [allDisplayedMessages, setAllDisplayedMessages] = useState<Message[]>([])

  /**
   * Queue message received from page and display in snack-bar for a short time
   * @param {*} message
   */
  const handleDisplayMessage = (message: Message) => {
    if (message.content) {
      // Add to message's content the amasis-code of the message if available
      const messageFullContent = message.content
        ? message.content +
          (message.code !== null && message.code !== undefined && message.code !== '' ? ' [' + message.code + ']' : '')
        : '-'
      // Prevent duplicates except for some specific messages which may require successive displays (ex.: man hour scanning of WO)
      enqueueSnackbar(message.content ? messageFullContent : '-', {
        variant: message.level,
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'center',
        },
        preventDuplicate: !message.enableDuplicates || false,
      })
      // Keep track of message in the History, even content's duplicates, but only if not too closed by date (below 1000ms)
      // We take this precaution since, in case the server is down for instance, all the widgets of a single page will send the same notification, which is annoying to the user (requested by Functionals).
      if (
        allDisplayedMessages.length === 0 ||
        (allDisplayedMessages.length > 0 &&
          (allDisplayedMessages[0].content !== message.content ||
            differenceInMilliseconds(message.date, allDisplayedMessages[0].date) >
              DELAY_BETWEEN_2_IDENTICAL_MESSAGES_IN_MS))
      ) {
        setAllDisplayedMessages([message].concat(allDisplayedMessages))
      }
    }
  }

  const handleClearMessages = () => {
    setAllDisplayedMessages([])
  }

  const handleToggleModalMessages = () => {
    setModalMessagesVisible(!isModalMessagesVisible)
  }

  return (
    <>
      <MessageContext.Provider
        value={{ handleDisplayMessage, handleToggleModalMessages, allDisplayedMessages, handleClearMessages }}
      >
        {children}
      </MessageContext.Provider>
      <ModalMessages
        isOpened={isModalMessagesVisible}
        onCloseModalMessages={handleToggleModalMessages}
        onClearMessages={handleClearMessages}
        messages={allDisplayedMessages}
      />
    </>
  )
}

export default MessageContext
